import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLogin from './components/AdminLogin';
import StatsCards from './components/StatsCards';
import ReservationTable from './components/ReservationTable';
import type { Reservation } from '../../mocks/reservations';
import { getReservations, updateReservationStatus } from '../../utils/reservationStorage';

export default function AdminPanel() {
  const [isAuth, setIsAuth] = useState(false);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'rezervasyonlar'>('dashboard');

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    const data = await getReservations();
    setReservations(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('admin_auth');
    if (stored === 'true') setIsAuth(true);
  }, []);

  useEffect(() => {
    if (isAuth) {
      fetchReservations();
    }
  }, [isAuth, fetchReservations]);

  const handleLogin = () => {
    setIsAuth(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    setIsAuth(false);
  };

  const handleStatusChange = async (id: string, status: Reservation['status']) => {
    await updateReservationStatus(id, status);

    // Onaylandı ise müşteriye e-posta gönder
    if (status === 'onaylandi') {
      const reservation = reservations.find((r) => r.id === id);
      if (reservation) {
        fetch(
          'https://gwbgizvobxzffikruire.supabase.co/functions/v1/send-reservation-email',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: reservation.email,
              firstName: reservation.firstName,
              lastName: reservation.lastName,
              tourName: reservation.tourName,
              departureDate: reservation.departureDate,
              totalPrice: reservation.totalPrice,
              currency: reservation.currency,
            }),
          }
        ).catch((err) => console.error('E-posta gönderilemedi:', err));
      }
    }

    setReservations((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status } : r))
    );
  };

  if (!isAuth) return <AdminLogin onLogin={handleLogin} />;

  const beklemede = reservations.filter((r) => r.status === 'beklemede').length;

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg">
                <i className="ri-shield-keyhole-line text-white text-sm" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Admin Paneli</p>
                <p className="text-xs text-gray-400">Ankara Travelers</p>
              </div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'bg-orange-50 text-orange-500'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-dashboard-line mr-1.5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('rezervasyonlar')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === 'rezervasyonlar'
                  ? 'bg-orange-50 text-orange-500'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-calendar-check-line" />
              Rezervasyonlar
              {beklemede > 0 && (
                <span className="bg-orange-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                  {beklemede}
                </span>
              )}
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchReservations}
              disabled={loading}
              className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              title="Rezervasyonları Yenile"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
              Yenile
            </button>
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              Siteye Git
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-600 hover:border-red-300 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line" />
              Çıkış
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      <div className="md:hidden flex border-b border-gray-100 bg-white px-4">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`flex-1 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'dashboard' ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-500'
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('rezervasyonlar')}
          className={`flex-1 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 ${
            activeTab === 'rezervasyonlar' ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-500'
          }`}
        >
          Rezervasyonlar
          {beklemede > 0 && (
            <span className="bg-orange-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full font-bold">
              {beklemede}
            </span>
          )}
        </button>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading && reservations.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-loader-4-line text-orange-500 text-2xl animate-spin" />
              </div>
              <p className="text-sm text-gray-500">Rezervasyonlar yükleniyor...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Genel Bakış</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Tüm rezervasyonların özeti</p>
                </div>
                <StatsCards reservations={reservations} />

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Son Rezervasyonlar</h3>
                    <button
                      onClick={() => setActiveTab('rezervasyonlar')}
                      className="text-xs text-orange-500 hover:text-orange-600 cursor-pointer whitespace-nowrap"
                    >
                      Tümünü Gör →
                    </button>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
                    {reservations.length === 0 ? (
                      <div className="text-center py-10">
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                          <i className="ri-inbox-line text-gray-400 text-lg" />
                        </div>
                        <p className="text-sm text-gray-400">Henüz rezervasyon yok</p>
                      </div>
                    ) : (
                      reservations.slice(0, 5).map((r) => (
                        <div key={r.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-orange-50/20 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full shrink-0">
                              <span className="text-xs font-bold text-gray-600">
                                {r.firstName[0]}{r.lastName[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{r.firstName} {r.lastName}</p>
                              <p className="text-xs text-gray-400 truncate max-w-[200px]">{r.tourName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-sm font-semibold text-gray-900 hidden sm:block">
                              {r.currency === 'TL' ? `₺${r.totalPrice.toLocaleString('tr-TR')}` : `€${r.totalPrice.toLocaleString('tr-TR')}`}
                            </p>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                              r.status === 'beklemede' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                              r.status === 'onaylandi' ? 'bg-green-50 text-green-600 border-green-200' :
                              'bg-red-50 text-red-500 border-red-200'
                            }`}>
                              {r.status === 'beklemede' ? 'Beklemede' : r.status === 'onaylandi' ? 'Onaylandı' : 'İptal'}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'rezervasyonlar' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Rezervasyonlar</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Tüm müşteri rezervasyon talepleri</p>
                </div>
                <ReservationTable reservations={reservations} onStatusChange={handleStatusChange} />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
